import { useState, useMemo, useRef } from 'react';
import api from '../../api';
import stili from './stili';

// Anteprima delle righe seriali: una riga per cliente, con l'esito del riconoscimento, il fatturante
// che ciascuna prenderà e gli eventuali duplicati. Non si crea nulla finché non si conferma.
const ESITO = {
  riconosciuto: { testo: 'Riconosciuto', colore: '#166534', sfondo: '#ecfdf5' },
  ambiguo: { testo: 'Ambiguo', colore: '#92400e', sfondo: '#fffbeb' },
  non_riconosciuto: { testo: 'Non riconosciuto', colore: '#b91c1c', sfondo: '#fef2f2' },
};

export default function AnteprimaSeriale({ anteprima, corpo, fatturanti, preimposta, onAnnulla, onCreate }) {
  const [righe, setRighe] = useState(() => anteprima.righe.map(r => ({
    ...r, fatturante_id: r.cliente ? String(preimposta(r) ?? r.fatturante_id ?? '') : '', escludi: false, creaComunque: false,
  })));
  const [errore, setErrore] = useState('');
  const [invio, setInvio] = useState(false);
  const occupato = useRef(false);   // doppio clic: lo stato React si aggiorna solo al ridisegno

  // Lo stesso cliente due volte: vale la prima riga, le altre sono segnate "ripetuto"
  const vista = useMemo(() => {
    const visti = new Set();
    return righe.map(r => {
      const ripetuto = Boolean(r.cliente) && !r.escludi && visti.has(r.cliente.id);
      if (r.cliente && !r.escludi && !ripetuto) visti.add(r.cliente.id);
      const daCreare = Boolean(r.cliente) && !r.escludi && !ripetuto && (!r.gia_presente || r.creaComunque);
      return { ...r, ripetuto, daCreare };
    });
  }, [righe]);

  const aggiorna = (i, cambi) => setRighe(l => l.map((r, j) => (j === i ? { ...r, ...cambi } : r)));
  const daRisolvere = vista.filter(r => !r.cliente && r.stato === 'ambiguo' && !r.escludi).length;
  const daCreare = vista.filter(r => r.daCreare);
  const giaPresentiSaltati = vista.filter(r => r.cliente && r.gia_presente && !r.creaComunque && !r.escludi && !r.ripetuto).length;
  const nonRiconosciuti = vista.filter(r => !r.cliente && r.stato === 'non_riconosciuto' && !r.escludi).length;

  // Scelta di un candidato: se ne rilegge fatturante e duplicato dal server
  const scegli = async (i, clienteId) => {
    if (!clienteId) return aggiorna(i, { cliente: null, gia_presente: null });
    try {
      const res = await api.post('/competenze/seriale/anteprima', { ...corpo, selezionati: [Number(clienteId)] });
      const nuova = res.data.righe[0];
      aggiorna(i, { cliente: nuova.cliente, gia_presente: nuova.gia_presente, fatturante_id: String(preimposta({ ...nuova, origine: 'incollato' }) ?? nuova.fatturante_id ?? ''), creaComunque: false });
    } catch (err) { setErrore(err.response?.data?.errore || 'Azienda non letta'); }
  };

  const motivoBlocco = daCreare.length === 0
    ? 'Nessuna riga da creare: nessuna azienda riconosciuta. Scegli i candidati o torna indietro.'
    : daRisolvere > 0 ? "Scegli l'azienda delle righe ambigue, oppure saltale." : '';

  const conferma = async () => {
    setErrore('');
    if (occupato.current) return;
    occupato.current = true;
    setInvio(true);
    try {
      const res = await api.post('/competenze/seriale', {
        ...corpo,
        clienti: daCreare.map(r => ({ cliente_id: r.cliente.id, fatturante_id: Number(r.fatturante_id) || null, senza_fatturante: !r.fatturante_id })),
        duplicati_confermati: daCreare.filter(r => r.gia_presente).map(r => r.cliente.id),
      });
      onCreate(res.data.create);
    } catch (err) {
      const d = err.response?.data;
      if (d?.codice === 'DUPLICATI') {
        const ids = new Set(d.duplicati.map(x => x.cliente_id));
        setRighe(l => l.map(r => (r.cliente && ids.has(r.cliente.id) ? { ...r, gia_presente: r.gia_presente || 1, creaComunque: false } : r)));
        setErrore(`${d.errore}. Non è stato creato nulla: ricontrolla le righe segnate "già presente".`);
      } else setErrore(d?.errore || 'Inserimento non riuscito: non è stata creata nessuna riga');
    } finally { occupato.current = false; setInvio(false); }
  };

  return (
    <div style={stili.overlay} role="dialog" aria-label="Anteprima righe">
      <div style={{ ...stili.modal, maxWidth: '1060px' }}>
        <h3 style={{ marginTop: 0 }}>Anteprima · {anteprima.righe.length} {anteprima.righe.length === 1 ? 'riga' : 'righe'}</h3>
        <p style={{ margin: '0 0 0.8rem', color: '#444', fontSize: '0.9rem' }}>
          Ogni cliente avrà una riga con data <strong>{anteprima.data.split('-').reverse().join('/')}</strong> e testo <strong>{anteprima.descrizione}</strong>.
          Non è ancora stato creato nulla.
        </p>
        <table style={stili.table}>
          <thead>
            <tr>
              <th style={stili.th}>Indicato</th><th style={stili.th}>Esito</th><th style={stili.th}>Azienda in archivio</th>
              <th style={stili.th}>Fatturante</th><th style={stili.th}>Duplicato</th><th style={stili.th}></th>
            </tr>
          </thead>
          <tbody>
            {vista.map((r, i) => {
              const esito = r.cliente ? ESITO.riconosciuto : ESITO[r.stato];
              return (
                <tr key={i} style={{ ...stili.tr, opacity: r.escludi || r.ripetuto ? 0.55 : 1 }}>
                  <td style={stili.td}>{r.origine === 'scelto' ? '—' : r.testo}</td>
                  <td style={stili.td}><span style={{ background: esito.sfondo, color: esito.colore, padding: '0.1rem 0.45rem', borderRadius: '4px', fontSize: '0.76rem', whiteSpace: 'nowrap' }}>{esito.testo}</span></td>
                  <td style={stili.td}>
                    {r.cliente ? <strong>{r.cliente.ragione_sociale}</strong> : r.stato === 'ambiguo' ? (
                      <div>
                        {r.candidati.length === 1 && (
                          <button type="button" onClick={() => scegli(i, r.candidati[0].id)} style={{ ...stili.btnSmall, display: 'block', marginBottom: '0.3rem', textAlign: 'left' }}>
                            Usa «{r.candidati[0].ragione_sociale}» ({r.candidati[0].somiglianza}%)
                          </button>
                        )}
                        <select value="" onChange={e => scegli(i, e.target.value)} style={stili.input} aria-label={`Scegli l'azienda per ${r.testo}`}>
                          <option value="">{r.candidati.length === 1 ? 'Un candidato simile: confermalo' : `Scegli tra ${r.candidati.length} candidati…`}</option>
                          {r.candidati.map(c => <option key={c.id} value={c.id}>{c.ragione_sociale}{c.codice_fiscale ? ` (${c.codice_fiscale})` : ''} · {c.somiglianza}%</option>)}
                        </select>
                      </div>
                    ) : <span style={{ color: '#b91c1c' }}>nessuna azienda con questo nome</span>}
                    {r.cliente && r.origine === 'incollato' && r.candidati.length === 0 && r.ripetuto && <div style={{ color: '#92400e', fontSize: '0.76rem' }}>già presente più sopra in questo elenco</div>}
                  </td>
                  <td style={stili.td}>
                    {r.cliente ? (
                      <select value={r.fatturante_id} onChange={e => aggiorna(i, { fatturante_id: e.target.value })} style={stili.input} aria-label={`Fatturante per ${r.cliente.ragione_sociale}`}>
                        <option value="">Nessun fatturante</option>
                        {fatturanti.map(f => <option key={f.id} value={f.id}>{f.nome}</option>)}
                      </select>
                    ) : '—'}
                  </td>
                  <td style={stili.td}>
                    {r.cliente && r.gia_presente && (
                      <label style={{ fontSize: '0.8rem', color: '#92400e' }}>
                        <strong>già presente</strong><br />
                        <input type="checkbox" checked={r.creaComunque} onChange={e => aggiorna(i, { creaComunque: e.target.checked })} /> Crea comunque
                      </label>
                    )}
                  </td>
                  <td style={stili.td}>
                    <button type="button" onClick={() => aggiorna(i, { escludi: !r.escludi })} style={r.escludi ? stili.btnSmall : stili.btnSmallDanger}>{r.escludi ? 'Includi' : 'Salta'}</button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <p style={{ fontSize: '0.88rem', margin: '0.8rem 0 0' }}>
          <strong>{daCreare.length}</strong> {daCreare.length === 1 ? 'riga verrà creata' : 'righe verranno create'}
          {giaPresentiSaltati > 0 && <>; <strong>{giaPresentiSaltati}</strong> già present{giaPresentiSaltati === 1 ? 'e non verrà creata' : 'i non verranno create'} (spunta "Crea comunque" per crearle)</>}
          {nonRiconosciuti > 0 && <>; <strong>{nonRiconosciuti}</strong> non {nonRiconosciuti === 1 ? 'riconosciuto resta fuori' : 'riconosciuti restano fuori'}</>}.
          {daRisolvere > 0 && <span style={{ color: '#92400e' }}> Scegli l'azienda delle {daRisolvere} righe ambigue, oppure saltale.</span>}
        </p>
        {daCreare.length === 0 && <div role="status" style={{ ...stili.errore, marginTop: '0.6rem' }}>{motivoBlocco}</div>}
        {errore && <div style={{ ...stili.errore, marginTop: '0.6rem' }}>{errore}</div>}
        <div style={stili.azioni}>
          <button type="button" onClick={onAnnulla} style={stili.btnSecondary}>Torna indietro</button>
          <button
            type="button" onClick={conferma} disabled={invio || daRisolvere > 0 || daCreare.length === 0}
            title={motivoBlocco} style={{ ...stili.btn, opacity: invio || daRisolvere > 0 || daCreare.length === 0 ? 0.45 : 1, cursor: daCreare.length === 0 ? 'not-allowed' : 'pointer' }}>
            {invio ? 'Creazione…' : `Conferma e crea ${daCreare.length} ${daCreare.length === 1 ? 'riga' : 'righe'}`}
          </button>
        </div>
      </div>
    </div>
  );
}
